import { datadogLogs } from '@datadog/browser-logs';
import * as Sentry from '@sentry/react';
import LogRocketConnection from 'logrocket';

import config from '@/config';
import { urls } from '@/constants';
import { ComplianceType, Plan, UserProfile } from '@/types';

import { isData, isError, isString } from './utils';

export const initLogger = () => {
  Sentry.init({
    dsn: config.SENTRY_DSN,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tunnel: urls.actions.sentry,
  });
};

interface InitProps {
  userId?: string;
  profile?: UserProfile;
  plan?: Plan;
  complianceType?: ComplianceType;
  registrationState?: boolean;
}

export const initSentry = ({
  userId,
  profile,
  plan,
  registrationState,
  complianceType,
}: InitProps) => {
  Sentry.setUser({ id: userId });

  if (!profile || !plan) return;

  Sentry.setUser({ id: userId, email: profile.email });
  Sentry.setContext('plan', plan);
  Sentry.setContext('user info', {
    complianceType,
    registrationState,
    ...profile,
  });
};

export const initLogRocket = ({
  userId,
  profile,
  complianceType,
}: InitProps) => {
  LogRocketConnection.init(config.LOGROCKET_PROJECT_KEY, {
    serverURL: urls.actions.logrocket,
    network: {
      requestSanitizer: request => {
        // Omit requests related to password updated
        if (request.url.toLowerCase().includes('password')) {
          return null;
        }

        return request;
      },
    },
  });

  const logRocketData = {
    name: profile ? `${profile.firstName} ${profile.lastName}` : '-',
    email: profile?.email || '-',
    complianceType: complianceType || '-',
    isAnonymous: !userId,
  };
  if (userId) {
    LogRocketConnection.identify(userId, logRocketData);
  } else {
    LogRocketConnection.identify(logRocketData);
  }

  LogRocketConnection.getSessionURL(sessionURL => {
    Sentry.getCurrentScope().setExtra('sessionURL', sessionURL);
  });
};

datadogLogs.init({
  clientToken: config.DATADOG_CLIENT_TOKEN,
  site: urls.actions.datadog,
  forwardErrorsToLogs: true,
  sessionSampleRate: 20,
  env: process.env.NODE_ENV,
  service: 'lumiq-web',
  version: config.GIT_COMMIT_SHA,
});

export const initDataDog = ({ userId, profile }: InitProps) => {
  if (!userId) return;

  const userData = {
    id: userId,
    email: profile ? profile.email : '-',
    name: profile ? `${profile.firstName} ${profile.lastName}` : '-',
  };

  datadogLogs.setUser(userData);
};

export const log = (...events: any[]) => {
  try {
    const messages = events.filter(isString);
    const objects = events.filter(isData);
    const errors = events.filter(isError);

    // breadcrumbs must be logged before exception is captured
    messages.forEach(m => logBreadcrumb({ message: m }));
    objects.forEach(o => logBreadcrumb({ data: o }));
    errors.forEach(logError);

    // fallback: if Sentry.captureException is never called, the breadcrumbs don't get logged
    if (errors.length === 0) {
      logError(new Error('No other error was provided'));
    }
  } catch (error) {
    logError(error as Error);
  }
};

const logError = (error: Error) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(error);
  }
  Sentry.captureException(error);
  datadogLogs.logger.error(error.message, {}, error);
};

export const logException = ({
  error,
  errorInfo,
  breadcrumb,
}: {
  error: unknown;
  errorInfo: React.ErrorInfo;
  breadcrumb?: string;
}) => {
  if (breadcrumb) {
    logBreadcrumb({ message: breadcrumb });
  }

  const err = new Error('FatalReactError', { cause: error });
  Sentry.captureException(err, scope => {
    scope.setLevel('fatal');
    scope.setContext('react', {
      componentStack: errorInfo.componentStack,
    });
    return scope;
  });
};

export const logBreadcrumb = ({
  message,
  data,
}: {
  message?: string;
  data?: Record<string, any>;
}) => {
  try {
    const payload = {
      message,
      level: 'debug' as Sentry.SeverityLevel,
      data,
    };

    Sentry.addBreadcrumb(payload);
  } catch (error) {
    Sentry.captureException(error);
  }
};
